import React, { useState, useEffect } from 'react';
import Swal from 'sweetalert2';
import './HomePageCMS.css';
import '../../styles/AdminShared.css';

const API = process.env.REACT_APP_API_URL || '/api';

interface Section { tag: string; tag_bn: string; title: string; title_bn: string; content: string; content_bn: string; }

interface PolicyContent {
  hero_title: string; hero_title_bn: string;
  hero_subtitle: string; hero_subtitle_bn: string;
  last_updated: string;
  sections: Section[];
}

const EMPTY: PolicyContent = {
  hero_title: '', hero_title_bn: '', hero_subtitle: '', hero_subtitle_bn: '',
  last_updated: '', sections: [],
};

interface Props {
  pageType: 'terms' | 'privacy';
  label: string;
  icon: string;
}

const PolicyPageCMS: React.FC<Props> = ({ pageType, label, icon }) => {
  const [content, setContent] = useState<PolicyContent>(EMPTY);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => { fetchContent(); }, [pageType]);

  const fetchContent = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const res = await fetch(`${API}/admin/cms/${pageType}/`, { headers: { Authorization: `Bearer ${token}` } });
      if (res.ok) setContent(await res.json());
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setContent(prev => ({ ...prev, [e.target.name]: e.target.value }));
  };

  /* Sections helpers */
  const addSection = () => setContent(prev => ({
    ...prev, sections: [...prev.sections, { tag: '', tag_bn: '', title: '', title_bn: '', content: '', content_bn: '' }],
  }));
  const removeSection = (i: number) => setContent(prev => ({
    ...prev, sections: prev.sections.filter((_, idx) => idx !== i),
  }));
  const updateSection = (i: number, field: keyof Section, v: string) => {
    const s = [...content.sections]; s[i] = { ...s[i], [field]: v };
    setContent(prev => ({ ...prev, sections: s }));
  };
  const moveSection = (i: number, dir: -1 | 1) => {
    const j = i + dir;
    if (j < 0 || j >= content.sections.length) return;
    const s = [...content.sections]; [s[i], s[j]] = [s[j], s[i]];
    setContent(prev => ({ ...prev, sections: s }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault(); setSaving(true);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API}/admin/cms/${pageType}/1/`, {
        method: 'PUT',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(content),
      });
      if (res.ok) { setContent(await res.json()); Swal.fire({ icon: 'success', title: 'Saved!', text: `${label} page updated`, timer: 1500, showConfirmButton: false }); }
      else throw new Error();
    } catch { Swal.fire({ icon: 'error', title: 'Error', text: 'Failed to save' }); }
    finally { setSaving(false); }
  };

  if (loading) return (
    <div className="homepage-cms-container"><div className="cms-card"><div className="card-content">
      <div className="cms-skeleton" style={{ height: 40 }} />
      <div className="cms-skeleton" style={{ height: 40, marginTop: 12 }} />
    </div></div></div>
  );

  return (
    <div className="homepage-cms-container">
      <form onSubmit={handleSubmit}>

        {/* Hero */}
        <div className="cms-card">
          <div className="card-header"><div className="card-header-left"><div className="card-icon icon-blue">{icon}</div><h3>Hero Section</h3></div></div>
          <div className="card-content">
            <div className="cms-grid-2">
              <div className="cms-form-group"><label>Hero Title (English)</label><input className="form-input" name="hero_title" value={content.hero_title} onChange={handleChange} /></div>
              <div className="cms-form-group"><label>Hero Title (??)</label><input className="form-input" name="hero_title_bn" value={content.hero_title_bn} onChange={handleChange} /></div>
            </div>
            <div className="cms-grid-2" style={{ marginTop: 8 }}>
              <div className="cms-form-group"><label>Hero Subtitle (English)</label><input className="form-input" name="hero_subtitle" value={content.hero_subtitle} onChange={handleChange} /></div>
              <div className="cms-form-group"><label>Hero Subtitle (??)</label><input className="form-input" name="hero_subtitle_bn" value={content.hero_subtitle_bn} onChange={handleChange} /></div>
            </div>
            <div className="cms-form-group" style={{ marginTop: 12 }}>
              <label>Last Updated Date</label>
              <input className="form-input" name="last_updated" value={content.last_updated} onChange={handleChange} placeholder="e.g. January 1, 2025" />
            </div>
          </div>
        </div>

        {/* Sections */}
        <div className="cms-card">
          <div className="card-header">
            <div className="card-header-left"><div className="card-icon icon-blue">{'\uD83D\uDCC4'}</div><h3>{label} Sections</h3></div>
            <button type="button" className="btn-add-feature" onClick={addSection}>+ Add Section</button>
          </div>
          <div className="card-content">
            {content.sections.length === 0 && <div className="empty-state"><p>No sections yet. Click "Add Section".</p></div>}
            {content.sections.map((sec, i) => (
              <div key={i} className="feature-item-card" style={{ marginBottom: 12 }}>
                <div className="feature-header">
                  <span className="feature-count">{'\u00A7'}{i + 1}</span>
                  <div style={{ display: 'flex', gap: 4 }}>
                    <button type="button" className="btn-remove-feature" onClick={() => moveSection(i, -1)} title="Move up" style={{ opacity: i === 0 ? 0.3 : 1 }}>{'\u25B2'}</button>
                    <button type="button" className="btn-remove-feature" onClick={() => moveSection(i, 1)} title="Move down" style={{ opacity: i === content.sections.length - 1 ? 0.3 : 1 }}>{'\u25BC'}</button>
                    <button type="button" className="btn-remove-feature" onClick={() => removeSection(i)}>{'\u2715'}</button>
                  </div>
                </div>
                <div className="cms-grid-2" style={{ marginBottom: 8 }}>
                  <div className="cms-form-group"><label>Tag / Short Label (English)</label><input className="form-input" value={sec.tag} onChange={e => updateSection(i, 'tag', e.target.value)} placeholder="e.g. information-collection" /></div>
                  <div className="cms-form-group"><label>Tag / Short Label (??)</label><input className="form-input" value={sec.tag_bn} onChange={e => updateSection(i, 'tag_bn', e.target.value)} /></div>
                </div>
                <div className="cms-grid-2" style={{ marginBottom: 8 }}>
                  <div className="cms-form-group"><label>Title (English)</label><input className="form-input" value={sec.title} onChange={e => updateSection(i, 'title', e.target.value)} /></div>
                  <div className="cms-form-group"><label>Title (??)</label><input className="form-input" value={sec.title_bn} onChange={e => updateSection(i, 'title_bn', e.target.value)} /></div>
                </div>
                <div className="cms-grid-2">
                  <div className="cms-form-group"><label>Content (English)</label><textarea className="form-input" value={sec.content} onChange={e => updateSection(i, 'content', e.target.value)} rows={5} style={{ resize: 'vertical' }} /></div>
                  <div className="cms-form-group"><label>Content (??)</label><textarea className="form-input" value={sec.content_bn} onChange={e => updateSection(i, 'content_bn', e.target.value)} rows={5} style={{ resize: 'vertical' }} /></div>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="cms-page-actions">
          <button type="submit" className="btn-save" disabled={saving}>{saving ? 'Saving...' : '\uD83D\uDCBE Save Changes'}</button>
        </div>
      </form>
    </div>
  );
};

export default PolicyPageCMS;
